import { useState, useMemo, useEffect, useRef, type Key } from 'react';
import { useNavigate } from 'react-router-dom';
import { ColumnDef, OnChangeFn, PaginationState } from '@tanstack/react-table';
import { Button, Tabs, Tab } from '@heroui/react';
import { CustomBreadcrumbs } from '@/widgets/CustomBreadcrumbs';
import {
  useGetTeacherTermsQuery,
  useGetTeacherTermModulesQuery,
  useGetTeacherTermStudentsQuery,
  ITeacherTermModule,
  ITeacherTermStudent,
  type TeacherTermModulesSortType,
  type TeacherTermStudentsSortType,
} from '@/entities/TeacherTerms';
import { useGetTeacherIntakesQuery } from '@/entities/TeacherIntakes';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import SearchIcon from '@/shared/assets/icons/search.svg';
import FilterIcon from '@/shared/assets/icons/filter.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { getTeacherTermDetailBreadcrumbs } from '@/shared/lib/utils/getTeacherCoursesBreadcrumbs';
import { routerPath } from '@/shared/config/router';
import { modulesColumns } from '../../config/modulesColumns';
import { studentsColumns } from '../../config/studentsColumns';
import { modulesSortOptions, studentsSortOptions } from '../../config/sortOptions';
import styles from './TermDetailView.module.scss';

interface TermDetailViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
  termId: number;
}

export const TermDetailView = ({
  courseId,
  intakeId,
  groupNumber,
  termId,
}: TermDetailViewProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('modules');
  const [modulesPagination, setModulesPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [studentsPagination, setStudentsPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 500);
  const [modulesSort, setModulesSort] = useState<string>('default');
  const [studentsSort, setStudentsSort] = useState<string>('default');
  const prevSearchRef = useRef(debouncedSearch);

  const { data: termsData, isLoading: isTermsLoading } = useGetTeacherTermsQuery({ intakeId });
  const { data: intakesData } = useGetTeacherIntakesQuery({ courseId, pageSize: 100 });

  const groupTitle = useMemo(() => {
    const intake = intakesData?.intakes?.find((i) => i.id === intakeId);
    const group = intake?.groups?.find((g) => g.number === groupNumber);
    return group?.title ?? null;
  }, [intakesData?.intakes, intakeId, groupNumber]);

  const term = useMemo(
    () => termsData?.terms.find((t) => t.id === termId),
    [termsData?.terms, termId],
  );
  const intakeTermId = term?.intakeTermId ?? null;

  const { data: modulesData, isLoading: isModulesLoading } = useGetTeacherTermModulesQuery(
    {
      groupNumber,
      intakeTermId: intakeTermId || 0,
      search: debouncedSearch || undefined,
      sort: modulesSort !== 'default' ? (modulesSort as TeacherTermModulesSortType) : undefined,
    },
    { skip: activeTab !== 'modules' || !intakeTermId },
  );
  const { data: studentsData, isLoading: isStudentsLoading } = useGetTeacherTermStudentsQuery(
    {
      groupNumber,
      intakeTermId: intakeTermId || 0,
      page: studentsPagination.pageIndex + 1,
      pageSize: studentsPagination.pageSize,
      search: debouncedSearch || undefined,
      sort: studentsSort !== 'default' ? (studentsSort as TeacherTermStudentsSortType) : undefined,
    },
    { skip: activeTab !== 'students' || !intakeTermId },
  );

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setModulesPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setStudentsPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const modules = useMemo(() => modulesData?.modules ?? [], [modulesData?.modules]);

  const students = useMemo(() => studentsData?.students ?? [], [studentsData?.students]);
  const studentsPaginationMeta = studentsData?.pagination;

  const firstModule = modules[0];

  const handleExport = () => {
    // TODO: Implement export
  };

  const handleModuleRowClick = (module: ITeacherTermModule) => {
    navigate(
      routerPath.teacherCourseGroupTermModuleLessons
        .replace(':courseId', courseId.toString())
        .replace(':intakeId', intakeId.toString())
        .replace(':groupNumber', groupNumber.toString())
        .replace(':termId', termId.toString())
        .replace(':intakeModuleId', module.intakeModuleId.toString()),
    );
  };

  const handleFilterModules = () => {
    // TODO: Implement filter modules
  };

  const handleCheckAttendance = () => {
    if (!firstModule) return;
    navigate(
      routerPath.teacherCourseGroupTermModuleAttendance
        .replace(':courseId', courseId.toString())
        .replace(':intakeId', intakeId.toString())
        .replace(':groupNumber', groupNumber.toString())
        .replace(':termId', termId.toString())
        .replace(':intakeModuleId', firstModule.intakeModuleId.toString()),
    );
  };

  if (isTermsLoading) {
    return <SectionLoader />;
  }

  if (!term) {
    return null;
  }

  const termLabel = `Group ${groupNumber} - Term ${term.number}`;
  const breadcrumbs = getTeacherTermDetailBreadcrumbs(
    courseId,
    intakeId,
    termsData?.course?.title ?? 'Course',
    groupNumber,
    groupTitle,
    termLabel,
  );

  return (
    <div className="flex flex-col gap-6 bg-white rounded-[24px] p-6 h-full min-h-0 flex-1">
      <CustomBreadcrumbs items={breadcrumbs} />

      <div className="flex items-center justify-between">
        <div className="inline-flex gap-x-2 items-end">
          <h2 className="text-[32px] font-medium">
            Group {groupNumber} - Term {term.number}
          </h2>
          <p className="text-grey-secondary leading-[220%]">
            (found{' '}
            {activeTab === 'modules'
              ? modules.length
              : studentsPaginationMeta?.total || students.length}{' '}
            {activeTab === 'modules' ? 'modules' : 'students'})
          </p>
        </div>
        <div className="flex items-center gap-4">
          <Button
            variant="bordered"
            className="border-[#211D55] bg-white text-[#211D55]"
            onPress={handleExport}
          >
            Export
          </Button>
          {activeTab === 'students' && (
            <Button
              variant="bordered"
              className="border-[#211D55] bg-white text-[#211D55]"
              onPress={handleCheckAttendance}
            >
              Check attendance
            </Button>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-4">
          <div className="max-w-[530px]">
            <CustomInput
              startContent={<Svg Icon={SearchIcon} />}
              placeholder="Search"
              value={search}
              onValueChange={setSearch}
            />
          </div>
        </div>

        <div className={styles.root}>
          <Tabs
            aria-label="Term detail tabs"
            selectedKey={activeTab}
            onSelectionChange={(key: Key) => setActiveTab(String(key))}
            classNames={{
              tabList: 'gap-0 bg-transparent',
              tab: 'h-9 min-h-9 px-3 font-medium bg-transparent text-grey-secondary data-[selected=true]:bg-[#1E1E1E] rounded-lg',
              panel: 'pt-6',
            }}
          >
            <Tab key="modules" title="Modules">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <Button isIconOnly className="bg-black-brand" onPress={handleFilterModules}>
                    <Svg Icon={FilterIcon} className="text-white" />
                  </Button>
                  <SelectSort
                    sections={modulesSortOptions}
                    defaultValue={modulesSort}
                    onSelectionChange={(keys) => {
                      const newValue = new Set(keys).values().next().value || 'default';
                      setModulesSort(newValue as string);
                    }}
                  />
                </div>
                {isModulesLoading ? (
                  <SectionLoader />
                ) : (
                  <ContainerTable<ITeacherTermModule>
                    caption="Modules in the term"
                    data={modules}
                    columns={modulesColumns}
                    pagination={modulesPagination}
                    setPagination={setModulesPagination as OnChangeFn<PaginationState>}
                    hideTopActions
                    onRowClick={handleModuleRowClick}
                  />
                )}
              </div>
            </Tab>
            <Tab key="students" title="Students">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <Button isIconOnly className="bg-black-brand" onPress={handleFilterModules}>
                    <Svg Icon={FilterIcon} className="text-white" />
                  </Button>
                  <SelectSort
                    sections={studentsSortOptions}
                    defaultValue={studentsSort}
                    onSelectionChange={(keys) => {
                      const newValue = new Set(keys).values().next().value || 'default';
                      setStudentsSort(newValue as string);
                    }}
                  />
                </div>
                {isStudentsLoading ? (
                  <SectionLoader />
                ) : (
                  <ContainerTable<ITeacherTermStudent>
                    caption="Students in the term"
                    data={students}
                    columns={studentsColumns() as ColumnDef<ITeacherTermStudent>[]}
                    pagination={studentsPagination}
                    setPagination={setStudentsPagination as OnChangeFn<PaginationState>}
                    hideTopActions
                    meta={
                      studentsPaginationMeta
                        ? {
                            page: studentsPaginationMeta.page,
                            pageSize: studentsPaginationMeta.pageSize,
                            total: studentsPaginationMeta.total,
                            pageCount: studentsPaginationMeta.pageCount,
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
